import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrand } from "@/hooks/use-brand";

export default function MarketingFooter() {
  const { t } = useTranslation();
  const brand = useBrand();
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border mt-32" data-testid="marketing-footer">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid grid-cols-2 md:grid-cols-6 gap-10">
        <div className="col-span-2 md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-7 w-7 rounded-md bg-primary text-primary-foreground grid place-items-center font-display font-bold text-sm">A</span>
            <span className="font-display text-base sm:text-lg tracking-tight">{brand.brand}</span>
          </div>
          <p className="text-sm text-muted-foreground max-w-xs">{t("footer.tagline")}</p>
        </div>

        <FooterCol title={t("footer.product")} links={[
          [t("footer.features"), "/features"],
          [t("footer.pricing"), "/pricing"],
          [t("footer.integrations"), "/integrations"],
          [t("footer.changelog"), "/changelog"],
        ]} />
        <FooterCol title={t("footer.company")} links={[
          [t("footer.about"), "/about"],
          [t("footer.customers"), "/customers"],
          [t("footer.careers"), "/careers"],
          [t("footer.press"), "/press"],
        ]} />
        <FooterCol title={t("footer.resources")} links={[
          [t("footer.help"), "/help"],
          [t("footer.blog"), "/blog"],
          [t("footer.community"), "/community"],
          [t("footer.contact"), "/contact"],
        ]} />
        <FooterCol title={t("footer.legal")} links={[
          [t("footer.privacy"), "/legal/privacy"],
          [t("footer.terms"), "/legal/terms"],
          [t("footer.hipaa"), "/legal/gdpr"],
          [t("footer.security"), "/security"],
        ]} />
      </div>

      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {t("footer.copyright", { year, brand: brand.brand })}
          </p>
          <p className="text-xs text-muted-foreground">{t("footer.madeWith")}</p>
        </div>
      </div>

      <div aria-hidden className="select-none overflow-hidden">
        <p className="font-display text-[12vw] sm:text-[18vw] leading-none tracking-tighter text-foreground/[0.04] -mb-[4vw] px-4">
          {brand.brandShort}
        </p>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {links.map(([label, href]) => (
          <li key={label}>
            <Link to={href} className="text-sm text-foreground/80 hover:text-foreground transition-colors">{label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
